import { api } from '../config/api';

export interface Usuario {
  id: string;
  id_firebase?: string;
  login: string;
  nome: string;
  email?: string;
  senha: string;
  senha_hash?: string;
  senha_salt?: string;
  perfil_id: string;
  primeiro_acesso: boolean;
  cnpj?: string;
  eh_transportadora?: boolean;
  filiais_permitidas?: string[];
  regiao?: string;
  telefone?: string;
  foto_perfil?: string;
  ativo: boolean;
  criado_em: string;
  atualizado_em: string;
}

export interface AuthUser {
  id: string;
  email?: string;
  nome: string;
  login: string;
  perfil: string;
  perfil_id?: string;
  filiais: string[];
  ativo: boolean;
  created_at: string;
  updated_at: string;
  primeiro_acesso?: boolean;
  foto_perfil?: string;
}

class UsuariosServiceAPI {
  // Autenticar usuário via API
  static async login(login: string, senha: string, captcha?: string): Promise<{ user: AuthUser | null; error: any; requiresCaptcha?: boolean }> {
    try {
      const response = await api.post('/auth/login', { login, senha, captcha });
      
      // Backend envia: { success: true, token, usuario }
      const data = response;
      
      if (data.token) {
        // Salvar access token no localStorage
        localStorage.setItem('@sistema-up:token', data.token);
        localStorage.setItem('@sistema-up:refreshToken', data.refreshToken || '');
        
        return {
          user: data.usuario,
          error: null,
          requiresCaptcha: false
        };
      }
      
      return { user: null, error: 'Falha na autenticação', requiresCaptcha: false };
    } catch (error: any) {
      console.error('Erro no login:', error);
      return {
        user: null,
        error: error.response?.data?.message || error.message || 'Erro ao fazer login',
        requiresCaptcha: error.response?.data?.requiresCaptcha || false
      };
    }
  }

  // Logout via API
  static async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('@sistema-up:refreshToken');
      await api.post('/auth/logout', { refreshToken });
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      // Remover token do localStorage
      localStorage.removeItem('@sistema-up:token');
      localStorage.removeItem('@sistema-up:refreshToken');
    }
  }

  // Refresh token
  static async refreshToken(): Promise<{ success: boolean; token?: string; refreshToken?: string; error?: string }> {
    try {
      const refreshToken = localStorage.getItem('@sistema-up:refreshToken');
      
      if (!refreshToken) {
        return { success: false, error: 'Refresh token não encontrado' };
      }
      
      const response = await api.post('/auth/refresh', { refreshToken });
      
      if (response.token) {
        localStorage.setItem('@sistema-up:token', response.token);
        localStorage.setItem('@sistema-up:refreshToken', response.refreshToken || '');
        
        return {
          success: true,
          token: response.token,
          refreshToken: response.refreshToken
        };
      }
      
      return { success: false, error: 'Falha ao renovar token' };
    } catch (error: any) {
      console.error('Erro ao renovar token:', error);
      
      // Se o refresh token falhar, remover tokens do localStorage
      localStorage.removeItem('@sistema-up:token');
      localStorage.removeItem('@sistema-up:refreshToken');
      
      return {
        success: false,
        error: error.response?.data?.error || 'Erro ao renovar token'
      };
    }
  }

  // Verificar token
  static async verificarToken(): Promise<AuthUser | null> {
    try {
      const response = await api.get('/auth/verify');
      return response.data.usuario;
    } catch (error) {
      console.error('Token inválido:', error);
      return null;
    }
  }

  // Buscar todos os usuários
  static async buscarTodos(filtros?: { perfil?: string[]; ativo?: boolean }): Promise<Usuario[]> {
    try {
      const params = new URLSearchParams();
      if (filtros?.perfil) {
        params.append('perfil', filtros.perfil.join(','));
      }
      if (filtros?.ativo !== undefined) {
        params.append('ativo', filtros.ativo.toString());
      }

      const response = await api.get(`/usuarios?${params.toString()}`);
      return response.data || [];
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      return [];
    }
  }

  // Buscar usuário por ID
  static async buscarPorId(id: string): Promise<Usuario | null> {
    try {
      const response = await api.get(`/usuarios/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar usuário:', error);
      return null;
    }
  }

  // Criar usuário
  static async criar(usuario: Omit<Usuario, 'id' | 'criado_em' | 'atualizado_em'>): Promise<Usuario> {
    try {
      const response = await api.post('/usuarios', usuario);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      throw error;
    }
  }

  // Atualizar usuário
  static async atualizar(id: string, updates: Partial<Usuario>): Promise<Usuario> {
    try {
      const response = await api.put(`/usuarios/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      throw error;
    }
  }

  // Deletar usuário
  static async deletar(id: string): Promise<void> {
    try {
      await api.delete(`/usuarios/${id}`);
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      throw error;
    }
  }

  // Verificar se email existe
  static async verificarEmailExistente(email: string): Promise<{ existe: boolean; error: any }> {
    try {
      // Buscar usuário pelo email
      const usuarios = await this.buscarTodos();
      const existe = usuarios.some(usuario => usuario.email === email);
      
      return { existe, error: null };
    } catch (error) {
      return { existe: false, error };
    }
  }

  // Buscar usuários por perfil
  static async buscarPorPerfil(perfil: string): Promise<Usuario[]> {
    return this.buscarTodos({ perfil: [perfil] });
  }

  // Buscar usuários ativos
  static async buscarAtivos(): Promise<Usuario[]> {
    return this.buscarTodos({ ativo: true });
  }

  // Ativar/Desativar usuário
  static async alternarStatus(id: string): Promise<Usuario> {
    try {
      const usuario = await this.buscarPorId(id);
      if (!usuario) {
        throw new Error('Usuário não encontrado');
      }

      const novoStatus = !usuario.ativo;
      return await this.atualizar(id, { ativo: novoStatus });
    } catch (error) {
      console.error('Erro ao alternar status do usuário:', error);
      throw error;
    }
  }

  // Resetar senha
  static async resetarSenha(id: string, novaSenha: string): Promise<void> {
    try {
      // Hash da senha (simulação - implementar hash real)
      await this.atualizar(id, { senha: novaSenha, primeiro_acesso: false });
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      throw error;
    }
  }

  // Atualizar perfil
  static async atualizarPerfil(id: string, perfil: string): Promise<Usuario> {
    return this.atualizar(id, { perfil_id: perfil });
  }

  // Atualizar filiais permitidas
  static async atualizarFiliais(id: string, filiais: string[]): Promise<Usuario> {
    return this.atualizar(id, { filiais_permitidas: filiais });
  }

  // Verificar se conta está bloqueada (apenas admin)
  static async verificarBloqueio(usuarioId: string): Promise<{ bloqueado: boolean; tipo?: string; motivo?: string; bloqueado_em?: string }> {
    try {
      const response = await api.get(`/auth/verificar-bloqueio/${usuarioId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar bloqueio:', error);
      return { bloqueado: false };
    }
  }

  // Desbloquear conta (apenas admin)
  static async desbloquearConta(usuarioId: string): Promise<void> {
    try {
      await api.post(`/auth/desbloquear-conta/${usuarioId}`, {});
    } catch (error) {
      console.error('Erro ao desbloquear conta:', error);
      throw error;
    }
  }
}

export default UsuariosServiceAPI;
